// src/components/ui/PageSpinner.jsx
/**
 * PageSpinner — the app's blocking loading overlay.
 *
 * Design contract
 * ───────────────
 * • All CSS lives in public/index.html (.nba-* rules) — zero runtime style
 *   injection, and every animated property is transform/opacity, so the whole
 *   overlay is composited off the main thread.
 * • Renders into the #spinner-root portal so it always clears MUI's stacking
 *   contexts (Dialog/Drawer/Popover) without a z-index arms race.
 *
 * What this component adds on top of "show a div"
 * ───────────────────────────────────────────────
 * 1. Anti-flicker `delay` — a request that resolves in 90ms never flashes a
 *    spinner at all. Nothing is mounted until the wait is worth announcing.
 * 2. Anti-flash `minDuration` — once shown, it stays up long enough to be read.
 *    A spinner that appears and vanishes in 40ms reads as a glitch, not as work.
 * 3. A real exit animation. `open → false` fades out and *then* unmounts,
 *    instead of the UI snapping.
 * 4. Single-owner rendering. If two spinners are open at once (route Suspense +
 *    a page action, which genuinely happens in NewApplication), only the most
 *    recently opened one paints. Two stacked backdrop-filters is the single
 *    most expensive thing this overlay could do; now it can't happen.
 * 5. Focus containment. Focus parks on the overlay while it's up, so Enter on a
 *    background button can't re-fire the action being waited on, and is handed
 *    back to the original element on close.
 * 6. A slow-wait hint after `slowAfter`, so a long backend call reads as
 *    "still working" rather than "hung".
 *
 * Usage (unchanged — every extra prop has a sensible default):
 *   import PageSpinner from '../ui/PageSpinner';
 *   <PageSpinner open={loading} message="Signing you in…" />
 */

import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/scrollLock';

/** Exit animation length. MUST match --nba-out in public/index.html. */
const EXIT_MS = 170;

const isBrowser = typeof document !== 'undefined';
const useIsoLayoutEffect = isBrowser ? useLayoutEffect : useEffect;
const now = () =>
  (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

/* ─────────────────────────────────────────────────────────────
   Portal root — resolved lazily, once, and re-resolved only if
   something detached it. (The old `useRef(<expression>)` form
   re-evaluated its initializer on every single render.)
   ───────────────────────────────────────────────────────────── */
let cachedRoot = null;
function getPortalRoot() {
  if (!isBrowser) return null;
  if (cachedRoot && cachedRoot.isConnected) return cachedRoot;
  cachedRoot = document.getElementById('spinner-root');
  if (!cachedRoot) {
    cachedRoot = document.createElement('div');
    cachedRoot.id = 'spinner-root';
    document.body.appendChild(cachedRoot);
  }
  return cachedRoot;
}

/* ─────────────────────────────────────────────────────────────
   Owner registry — the last spinner to become visible is the one
   that paints. Everyone else stays mounted (so their timers and
   scroll locks stay correct) but renders nothing.
   ───────────────────────────────────────────────────────────── */
const ownerStack = [];
const ownerListeners = new Set();
const emitOwner = () => ownerListeners.forEach((fn) => fn());
const getOwner = () => (ownerStack.length ? ownerStack[ownerStack.length - 1] : null);
const getOwnerServer = () => null;
function subscribeOwner(fn) {
  ownerListeners.add(fn);
  return () => ownerListeners.delete(fn);
}
function acquireOwner(id) {
  if (!ownerStack.includes(id)) { ownerStack.push(id); emitOwner(); }
}
function releaseOwner(id) {
  const i = ownerStack.indexOf(id);
  if (i !== -1) { ownerStack.splice(i, 1); emitOwner(); }
}

let seq = 0;

/* ─────────────────────────────────────────────────────────────
   Presentation — pure, memoized, no state of its own.
   ───────────────────────────────────────────────────────────── */
const SpinnerOverlay = memo(function SpinnerOverlay({
  state, message, hint, label, overlayRef, onKeyDownCapture, manageFocus,
}) {
  return (
    <div
      ref={overlayRef}
      className="nba-overlay"
      data-state={state}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label || message || 'Loading, please wait'}
      tabIndex={manageFocus ? -1 : undefined}
      onKeyDownCapture={onKeyDownCapture}
    >
      <div className="nba-spinner-card">
        <div className="nba-ring" aria-hidden="true">
          <div className="nba-ring-arc" />
          <div className="nba-ring-core" />
        </div>

        {/* Keyed so a changed message replays its own fade-in */}
        <span key={message} className="nba-spinner-msg">
          {message || 'Please wait…'}
        </span>

        {hint ? <span className="nba-spinner-hint">{hint}</span> : null}
      </div>
    </div>
  );
});

/* ───────────────────────────────────────────────────────────── */
export default function PageSpinner({
  open = false,
  message,
  /** Wait this long before showing anything — kills sub-perceptual flashes. */
  delay = 140,
  /** Once shown, stay up at least this long. */
  minDuration = 420,
  /** Show `slowMessage` after this long. 0 disables. */
  slowAfter = 9000,
  slowMessage = 'Still working — this is taking a little longer than usual.',
  /** Park focus on the overlay while it's up (blocks background key activation). */
  manageFocus = true,
  /** Accessible name override; defaults to `message`. */
  label,
}) {
  // 'idle' → 'visible' → 'closing' → 'idle'
  const [phase, setPhase] = useState('idle');
  const [slow, setSlow] = useState(false);

  const phaseRef = useRef('idle');
  const shownAtRef = useRef(0);
  const overlayRef = useRef(null);
  const restoreFocusRef = useRef(null);

  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = ++seq;

  const setPhaseSafe = useCallback((next) => {
    if (phaseRef.current === next) return;
    phaseRef.current = next;
    setPhase(next);
  }, []);

  /* ── Timing state machine ──────────────────────────────────
     Deliberately not keyed on `phase`: the effect drives phase,
     phase must not re-drive the effect. */
  useEffect(() => {
    let showT, holdT, exitT;

    if (open) {
      const show = () => { shownAtRef.current = now(); setPhaseSafe('visible'); };
      if (phaseRef.current === 'visible') {
        /* already up — nothing to do */
      } else if (phaseRef.current === 'closing' || delay <= 0) {
        show();                                   // re-opened mid-exit: no second delay
      } else {
        showT = setTimeout(show, delay);
      }
    } else if (phaseRef.current === 'visible') {
      const remaining = Math.max(0, minDuration - (now() - shownAtRef.current));
      holdT = setTimeout(() => {
        setPhaseSafe('closing');
        exitT = setTimeout(() => setPhaseSafe('idle'), EXIT_MS);
      }, remaining);
    } else if (phaseRef.current !== 'idle') {
      setPhaseSafe('idle');
    }

    return () => {
      clearTimeout(showT);
      clearTimeout(holdT);
      clearTimeout(exitT);
    };
  }, [open, delay, minDuration, setPhaseSafe]);

  const mounted = phase === 'visible' || phase === 'closing';

  /* ── Own the paint slot while mounted (layout-phase, so there's
        no wasted frame where nobody renders) ── */
  useIsoLayoutEffect(() => {
    if (!mounted) return undefined;
    const id = idRef.current;
    acquireOwner(id);
    return () => releaseOwner(id);
  }, [mounted]);

  const owner = useSyncExternalStore(subscribeOwner, getOwner, getOwnerServer);
  const isOwner = owner === idRef.current;

  /* ── Background scroll lock (reference-counted in scrollLock.js) ── */
  useEffect(() => {
    if (!mounted) return undefined;
    return lockBodyScroll();
  }, [mounted]);

  /* ── Slow-wait hint ── */
  useEffect(() => {
    if (phase === 'idle') { setSlow(false); return undefined; }
    // Held through 'closing' on purpose — the hint shouldn't blink out a beat
    // before the card it sits in.
    if (phase !== 'visible' || !slowAfter) return undefined;
    const t = setTimeout(() => setSlow(true), slowAfter);
    return () => clearTimeout(t);
  }, [phase, slowAfter]);

  /* ── Focus containment: park focus here, hand it back on close ── */
  useEffect(() => {
    if (!manageFocus || !isOwner || phase !== 'visible' || !isBrowser) return undefined;

    const overlay = overlayRef.current;          // captured now; it's gone by cleanup
    const previous = document.activeElement;
    restoreFocusRef.current =
      previous && previous !== document.body ? previous : null;
    overlay?.focus({ preventScroll: true });

    return () => {
      const el = restoreFocusRef.current;
      restoreFocusRef.current = null;
      // Only hand focus back if nothing else has legitimately claimed it
      // (a route that mounted and autofocused its first field, say).
      const active = document.activeElement;
      const stillOurs = !active || active === document.body || active === overlay;
      if (stillOurs && el && el.isConnected && typeof el.focus === 'function') {
        el.focus({ preventScroll: true });
      }
    };
  }, [manageFocus, isOwner, phase]);

  /* Keep Tab from walking into the UI behind the scrim. */
  const onKeyDownCapture = useCallback((e) => {
    if (manageFocus && e.key === 'Tab') e.preventDefault();
  }, [manageFocus]);

  if (!mounted || !isOwner) return null;

  const portalRoot = getPortalRoot();
  if (!portalRoot) return null;

  return createPortal(
    <SpinnerOverlay
      state={phase}
      message={message}
      hint={slow ? slowMessage : null}
      label={label}
      overlayRef={overlayRef}
      manageFocus={manageFocus}
      onKeyDownCapture={onKeyDownCapture}
    />,
    portalRoot
  );
}
